// Şub 27, 2026 — DPD Retour Isı Haritası
// Posta kodu (ilk 2 hane) × sebep kodu matrisi
import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { useTranslation } from '../../lib/i18n';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { Flame, RefreshCw, Loader2, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

// Değere göre gradient (0..max) — sarı → turuncu → kırmızı
const cellStyle = (count, max) => {
  if (!count) return { bg: 'transparent', color: '#9ca3af' };
  const ratio = Math.min(count / Math.max(max, 1), 1);
  // Amber to red gradient
  const hue = 45 - ratio * 45; // 45 (amber) → 0 (red)
  const light = 60 - ratio * 25; // 60% → 35%
  const sat = 85;
  return {
    bg: `hsl(${hue}, ${sat}%, ${light}%)`,
    color: ratio > 0.5 ? '#fff' : '#111',
  };
};

export default function DPDRetourHeatmap() {
  const { t } = useTranslation();
  const [firms, setFirms] = useState([]);
  const [firmId, setFirmId] = useState('');
  const [dateFrom, setDateFrom] = useState(daysAgoIso(30));
  const [dateTo, setDateTo] = useState(todayIso());
  const [subco, setSubco] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/admin/firms').then((r) => {
      const list = r.data?.data || [];
      setFirms(list);
      const ft = list.find((f) => /fast.?trans/i.test(f.name)) || list[0];
      if (ft) setFirmId(ft.id);
    }).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    if (!firmId || !dateFrom || !dateTo) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ firm_id: firmId, date_from: dateFrom, date_to: dateTo });
      if (subco) params.set('subco', subco);
      const r = await api.get(`/dpd/retour-heatmap?${params.toString()}`);
      setData(r.data?.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Isı haritası yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [firmId, dateFrom, dateTo, subco]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const cells = data?.cells || [];
  const postcodes = data?.postcodes || [];
  const codes = data?.codes || [];
  const matrix = data?.matrix || {};

  // Max value for gradient
  const maxCount = cells.reduce((m, c) => Math.max(m, c.count), 0);

  const setPreset = (n) => {
    setDateFrom(daysAgoIso(n));
    setDateTo(todayIso());
  };

  return (
    <div className="space-y-4" data-testid="dpd-heatmap-page">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Flame className="h-5 w-5 text-orange-600" />
            {t('dpd.heatmap.title')}
          </CardTitle>
          <CardDescription>
            {t('dpd.heatmap.subtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-3">
            <Select value={firmId} onValueChange={setFirmId}>
              <SelectTrigger data-testid="hm-firm-select"><SelectValue placeholder="Firma" /></SelectTrigger>
              <SelectContent>
                {firms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="hm-date-from" />
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="hm-date-to" />
            <Input placeholder={t('dpd.heatmap.subcoFilter')} value={subco} onChange={(e) => setSubco(e.target.value)} data-testid="hm-subco" />
            <Button onClick={fetchData} disabled={loading} variant="outline" data-testid="hm-refresh-btn">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              {t('dpd.common.refresh')}
            </Button>
          </div>

          <div className="flex gap-2 mb-4 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => setPreset(7)}>{t('dpd.common.preset7')}</Button>
            <Button size="sm" variant="outline" onClick={() => setPreset(30)}>{t('dpd.common.preset30')}</Button>
            <Button size="sm" variant="outline" onClick={() => setPreset(90)}>{t('dpd.common.preset90')}</Button>
            <Button size="sm" variant={subco === 'fast-trans' ? 'default' : 'outline'} onClick={() => setSubco(subco === 'fast-trans' ? '' : 'fast-trans')} data-testid="hm-ft-toggle">
              {subco === 'fast-trans' ? t('dpd.heatmap.clearFilter') : t('dpd.heatmap.ftOnly')}
            </Button>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-4">
            <StatBox label={t('dpd.heatmap.stats.totalRetour')} value={data?.grand_total || 0} icon={Flame} color="red" />
            <StatBox label={t('dpd.heatmap.stats.pcRegion')} value={postcodes.length} icon={MapPin} color="blue" />
            <StatBox label={t('dpd.heatmap.stats.reasonCodes')} value={codes.length} icon={Flame} color="purple" />
          </div>

          {loading && <Skeleton className="h-64 w-full" />}
          {!loading && cells.length === 0 && (
            <div className="text-center text-gray-500 py-12 border rounded-lg">
              {t('dpd.heatmap.empty')}
            </div>
          )}

          {!loading && cells.length > 0 && (
            <>
              <div className="border rounded-lg overflow-auto">
                <table className="text-xs w-full">
                  <thead className="sticky top-0 bg-gray-50 dark:bg-gray-900 z-10">
                    <tr>
                      <th className="sticky left-0 bg-gray-50 dark:bg-gray-900 p-2 text-left border-r min-w-[180px]">
                        PC / Bölge
                      </th>
                      {codes.map((c) => (
                        <th key={c.code} className="p-1 text-center min-w-[52px] border-r" title={c.label}>
                          <Badge variant="outline" className="font-mono text-[10px] block">
                            {c.code || '—'}
                          </Badge>
                          <div className="text-[9px] text-gray-500 mt-0.5">{c.total}</div>
                        </th>
                      ))}
                      <th className="p-2 text-center bg-gray-100 dark:bg-gray-800 border-l">Toplam</th>
                    </tr>
                  </thead>
                  <tbody>
                    {postcodes.map((pc) => (
                      <tr key={pc.prefix} data-testid={`heatmap-row-${pc.prefix}`}>
                        <td className="sticky left-0 bg-white dark:bg-gray-950 p-2 border-r border-t">
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="font-mono">{pc.prefix}</Badge>
                            <span className="text-[11px] text-gray-600 dark:text-gray-400 truncate max-w-[130px]" title={pc.region}>
                              {pc.region}
                            </span>
                          </div>
                        </td>
                        {codes.map((c) => {
                          const cnt = matrix[pc.prefix]?.[c.code] || 0;
                          const style = cellStyle(cnt, maxCount);
                          return (
                            <td
                              key={c.code}
                              className="text-center border-t border-r p-0"
                              title={`PC ${pc.prefix} — ${c.label}: ${cnt} paket`}
                            >
                              {cnt > 0 ? (
                                <div
                                  className="w-full h-9 flex items-center justify-center font-semibold"
                                  style={{ background: style.bg, color: style.color }}
                                >
                                  {cnt}
                                </div>
                              ) : (
                                <div className="w-full h-9" />
                              )}
                            </td>
                          );
                        })}
                        <td className="text-center border-t border-l bg-gray-50 dark:bg-gray-900 font-bold p-2">
                          {pc.total}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center gap-3 mt-3 text-[11px] text-gray-600 dark:text-gray-400">
                <span>Yoğunluk:</span>
                <div className="flex items-center gap-1">
                  {[0.1, 0.3, 0.5, 0.7, 0.9].map((r) => {
                    const style = cellStyle(r * maxCount, maxCount);
                    return <div key={r} className="w-6 h-4 rounded" style={{ background: style.bg }} />;
                  })}
                </div>
                <span>Düşük → Yüksek</span>
                <span className="ml-4">Max: {maxCount} paket / hücre</span>
              </div>

              <div className="mt-4">
                <h4 className="text-sm font-semibold mb-2">{t('dpd.heatmap.reasonLegendTitle')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1 text-[11px]">
                  {codes.map((c) => (
                    <div key={c.code} className="flex items-center gap-2">
                      <Badge variant="outline" className="font-mono text-[10px]">{c.code || '—'}</Badge>
                      <span className="text-gray-700 dark:text-gray-300">{c.label}</span>
                      <span className="text-gray-500">({c.total})</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatBox({ label, value, icon: Icon, color }) {
  const colors = {
    red: 'bg-red-50 text-red-800',
    blue: 'bg-blue-50 text-blue-800',
    purple: 'bg-purple-50 text-purple-800',
  };
  return (
    <div className={`rounded-lg p-3 ${colors[color]}`}>
      <div className="flex items-center gap-2 text-xs opacity-80"><Icon className="h-4 w-4" />{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
